import type {ReactElement} from 'react';
import React from 'react';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {BaseGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option';
import {GammaColorType} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option/base-gamma-option.types';
import {useAppSelector} from '@/hooks';

export function ImageBgGammaOption(): ReactElement<any> | null {
  const currentPageObject = useAppSelector(getCurrentPage);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  if (!currentPageObject) {
    return null;
  }

  if (currentPageObject.background.details.type !== BackgroundTypeName.IMAGE) {
    return null;
  }

  const onToggleGamma = (): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject({
        details: {
          imageBackgroundItemObject: {
            effects: {
              gamma: {
                isEnabled: !currentPageObject.background.details.imageBackgroundItemObject.effects.gamma.isEnabled,
              },
            },
          },
        },
      });
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateGreenGammaValue = (green: number, undoable = true): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                gamma: {
                  green,
                },
              },
            },
          },
        },
        {undoable}
      );
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateRedGammaValue = (red: number, undoable = true): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                gamma: {
                  red,
                },
              },
            },
          },
        },
        {undoable}
      );
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateBlueGammaValue = (blue: number, undoable = true): void => {
    if ('imageBackgroundItemObject' in currentPageObject.background.details) {
      void currentPage?.background.updateFromObject(
        {
          details: {
            imageBackgroundItemObject: {
              effects: {
                gamma: {
                  blue,
                },
              },
            },
          },
        },
        {undoable}
      );
    } else {
      throw new Error('imageBackgroundItemObject does not exist in page background details');
    }
  };

  const onUpdateColor = (color: GammaColorType, value: number, undoable = true): void => {
    switch (color) {
      case GammaColorType.GREEN:
        return onUpdateGreenGammaValue(value, undoable);
      case GammaColorType.RED:
        return onUpdateRedGammaValue(value, undoable);
      case GammaColorType.BLUE:
        return onUpdateBlueGammaValue(value, undoable);
      default:
        throw new Error('unhandled color for gamma in image background gamma options');
    }
  };

  return <BaseGammaOption gamma={currentPageObject.background.details.imageBackgroundItemObject.effects.gamma} updateColor={onUpdateColor} onToggleGamma={onToggleGamma} />;
}
